import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Magnetic } from '@/components/generals/magnetic'

interface CtaProps {
    href: string
    children: ReactNode
    variant?: 'primary' | 'ghost'
    size?: 'sm' | 'md' | 'lg'
    arrow?: boolean
    /** Opens in a new tab and marks the link as leaving the site. */
    newTab?: boolean
    magnetic?: boolean
    className?: string
    cursor?: string
}

const isExternal = (href: string): boolean => /^(https?:|mailto:|tel:)/.test(href)

export const Cta: FC<CtaProps> = ({
    href,
    children,
    variant = 'primary',
    size = 'md',
    arrow = true,
    newTab = false,
    magnetic = false,
    className,
    cursor
}): ReactNode => {
    const classes = cn(
        'btn',
        variant === 'ghost' && 'btn-ghost',
        size === 'lg' && 'btn-lg',
        size === 'sm' && 'btn-sm',
        className
    )

    const Icon = newTab ? ArrowUpRight : ArrowRight

    const content = (
        <>
            <span>{children}</span>
            {arrow && <Icon className='btn-arrow size-4 shrink-0' aria-hidden='true' />}
            {newTab && <span className='sr-only'> (åpnes i ny fane)</span>}
        </>
    )

    const element = isExternal(href)
        ? (
            <a
                href={href}
                className={classes}
                data-cursor={cursor}
                {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
                {content}
            </a>
        )
        : (
            <Link href={href} className={classes} data-cursor={cursor}>
                {content}
            </Link>
        )

    return magnetic ? <Magnetic>{element}</Magnetic> : element
}

interface ArrowLinkProps {
    href: string
    children: ReactNode
    className?: string
    newTab?: boolean
}

export const ArrowLink: FC<ArrowLinkProps> = ({ href, children, className, newTab = false }): ReactNode => {
    const classes = cn('arrow-link group inline-flex items-center gap-2 font-semibold', className)
    const Icon = newTab ? ArrowUpRight : ArrowRight

    const content = (
        <>
            <span className='u-link-static pb-0.5'>{children}</span>
            <Icon className='arrow-link-icon size-4 shrink-0' aria-hidden='true' />
            {newTab && <span className='sr-only'> (åpnes i ny fane)</span>}
        </>
    )

    if (isExternal(href)) {
        return (
            <a href={href} className={classes} {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                {content}
            </a>
        )
    }

    return (
        <Link href={href} className={classes}>
            {content}
        </Link>
    )
}
